import { useContext, useState, useEffect, VFC } from "react";
import { DateTime } from "luxon";
// Libraries
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
// Api
import { CREATE_GROUP, UPDATE_GROUP } from "api/MyLivestock/Groups/mutations";
import { GET_MY_ANIMALS_LIST } from "api/MyLivestock/Animal/queries";
import { GET_GROUPS_LIST } from "api/MyLivestock/Groups/queries";
import { GetAnimalsList, GetAnimalsListVariables } from "api/MyLivestock/Animal/types/GetAnimalsList";
import { CreateGroup, CreateGroupVariables } from "api/MyLivestock/Groups/types/CreateGroup";
import { UpdateGroup, UpdateGroupVariables } from "api/MyLivestock/Groups/types/UpdateGroup";
// Utils
import { CommonContext } from "config/commonProvider";

import { GROUPS, GROUP_VIEW } from "constants/Routes";
import { HEADER_TYPES } from "constants/General";
import { GROUP_CATEGORY_OPTIONS } from "constants/Groups";

import { getBUFromLocalStorage } from "helpers/storage";
import { handleErrorCheck } from "helpers/general";
// Hooks
import { useGetAnimalTypes, useGetGroupById, useEventRequest, useStateSpecies } from "hooks";
// Components
import GroupHeader from "pages/MyLivestock/General/GroupFieldHeader";
import AssignAnimalsToGroup from "pages/MyLivestock/Groups/AssignAnimalsToGroup";
import {
  PageHeader,
  PageContent,
  Spacer,
  LoadingOverlay,
  Breadcrumbs,
  FieldNumber,
  FieldDate,
  FieldText,
  Flex,
  Title,
  FieldSelect,
} from "components";
// Resources
import styles from "./CreateEditGroup.module.scss";

type FormValues = {
  name: string;
  targetWeight: string;
  targetDate: Date | string;
  category: string;
};

type FormProps = FormikProps<FormValues>;

const validationSchemaCreateGroup = Yup.object().shape({
  name: Yup.string().required("Group name is required!"),
  targetWeight: Yup.string().required("Target weight name is required!"),
  targetDate: Yup.string(),
  category: Yup.string().required("Type is required!"),
});

const CreateEditGroup: VFC = () => {
  const { active: speciesActive } = useStateSpecies();

  const { groupId } = useParams<LivestockGroupPageParams>();
  const { data: groupsData, loading: getGroupLoading } = useGetGroupById(groupId);
  const [updateGroupMutation, { loading: updateGroupLoading }] = useMutation<UpdateGroup, UpdateGroupVariables>(
    UPDATE_GROUP,
  );

  const history = useHistory();
  const [isEdited, changeEditMode] = useState<string | null>(null);

  const { showNotification } = useContext(CommonContext);
  const { eventsRequest } = useEventRequest();

  const { data: animalTypesData } = useGetAnimalTypes();

  const [createGroupMutation, { data: createGroupData, loading: createGroupLoading }] = useMutation<
    CreateGroup,
    CreateGroupVariables
  >(CREATE_GROUP);

  const animalGroup = groupsData?.group;
  const name = animalGroup?.name || "";
  const targetWeight = animalGroup?.targetWeight ? `${animalGroup?.targetWeight}` : "";
  const category = animalGroup?.category || GROUP_CATEGORY_OPTIONS[3].value;
  const animalTypes = animalTypesData?.animalTypes;
  const animalTypeCattle = animalTypes?.find((item) => item?.slug === "cattle");
  const targetDate = animalGroup?.targetDate ? DateTime.fromISO(animalGroup.targetDate).toJSDate() : "";
  const businessUnitId = getBUFromLocalStorage();

  useEffect(() => {
    if (createGroupData && createGroupData?.createGroup?.group) {
      history.push(`${GROUP_VIEW}/${createGroupData.createGroup.group.id}`);
    }
  }, [history, createGroupData]);

  const showActionResult = (errors): void => {
    if (errors) {
      showNotification({
        variant: "error",
        message: errors.map((item) => item?.message).join("; "),
      });
    } else {
      if (!animalGroup) {
        eventsRequest({ name: "group-create" });
      }

      history.push(`${GROUP_VIEW}/${animalGroup?.id}`);
      showNotification({
        message: `Group successfully ${animalGroup ? "updated" : "created"}`,
      });
    }
  };

  const handleSaveGroup = async (values: FormValues): Promise<void> => {
    const input = {
      name: values.name,
      targetWeight: Number(values.targetWeight),
      targetDate: typeof values.targetDate !== "string" ? DateTime.fromJSDate(values.targetDate).toISODate() : null,
      animalType: Number(speciesActive?.id || animalTypeCattle?.id),
      category: values.category,
    };

    try {
      if (animalGroup) {
        const { data } = await updateGroupMutation({
          variables: {
            input: {
              ...input,
              id: animalGroup.id,
            },
          },
          update: (cache, { data }) => {
            if (data?.updateGroup?.group) {
              const previousData = cache.readQuery<GetAnimalsList, GetAnimalsListVariables>({
                query: GET_MY_ANIMALS_LIST,
                variables: {
                  businessUnitId,
                },
              });

              const animalsExtended = previousData?.animalsExtended;
              const animals = animalsExtended?.animals;

              if (animalsExtended && animals) {
                const updatedAnimalsData = animals.map((item) => {
                  if (item?.group && item.group.id === animalGroup.id) {
                    return {
                      ...item,
                      group: {
                        ...item.group,
                        name: input.name,
                      },
                    };
                  }

                  return item;
                });

                cache.writeQuery<GetAnimalsList, GetAnimalsListVariables>({
                  query: GET_MY_ANIMALS_LIST,
                  variables: {
                    businessUnitId,
                  },
                  data: {
                    ...previousData,
                    animalsExtended: {
                      ...animalsExtended,
                      animals: updatedAnimalsData,
                    },
                  },
                });
              }
            }
          },
        });

        showActionResult(data?.updateGroup?.errors);
      } else {
        const { data } = await createGroupMutation({
          variables: {
            input: {
              ...input,
              businessUnit: businessUnitId,
            },
          },
          refetchQueries: ({ data }) => {
            if (data?.createGroup && !data.createGroup.errors) {
              return [
                {
                  query: GET_GROUPS_LIST,
                  variables: {
                    businessUnitId,
                  },
                },
              ];
            }

            return [];
          },
        });

        showActionResult(data?.createGroup?.errors);
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: `Error ${animalGroup ? "updating" : "creating"} group`,
      });
    }
  };

  const initialValues = {
    name,
    targetWeight,
    category,
    targetDate,
  };

  if (speciesActive) {
    const isGroupDifferentSpecies = animalGroup && animalGroup.animalType?.id !== speciesActive.id;
    if (isGroupDifferentSpecies) return <Redirect to={GROUPS} />;
  }

  return (
    <>
      {(getGroupLoading || updateGroupLoading || createGroupLoading) && (
        <LoadingOverlay customStyles={styles.loadingOverlay} />
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchemaCreateGroup}
        onSubmit={handleSaveGroup}
      >
        {({ values, errors, handleChange, setFieldValue, handleSubmit }: FormProps): React.ReactNode => {
          const handleTargetDateChange = (value: string): void => {
            setFieldValue("targetDate", value);
          };

          return (
            <>
              <PageHeader>
                <Breadcrumbs
                  rootPaths={[
                    {
                      path: "Groups",
                      route: GROUPS,
                    },
                  ]}
                  currentPath={animalGroup ? "Update Group" : "Create Group"}
                />

                <GroupHeader type={HEADER_TYPES.GROUP} item={animalGroup} handleSubmit={handleSubmit} />
              </PageHeader>

              <PageContent>
                <Spacer baselineHeight={3} />

                <Flex container>
                  <Flex item itemGutter xs={12}>
                    <Title secondary>Group details</Title>
                  </Flex>
                </Flex>
                <Spacer baselineHeight={1} />
                <Flex container>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldText
                      label="Group name"
                      error={errors.name}
                      inputProps={{
                        name: "name",
                        defaultValue: values.name,
                        onChange: handleChange,
                      }}
                    />
                  </Flex>

                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldNumber
                      label="Target weight"
                      error={handleErrorCheck(errors.targetWeight)}
                      inputProps={{
                        name: "targetWeight",
                        onChange: handleChange,
                        value: values.targetWeight,
                      }}
                    />
                  </Flex>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldDate
                      label="Target Date"
                      error={handleErrorCheck(errors.targetDate)}
                      inputProps={{
                        name: "targetDate",
                        value: values.targetDate,
                        onChange: handleTargetDateChange,
                        minDate: new Date(),
                      }}
                    />
                  </Flex>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldSelect
                      label="Type"
                      error={handleErrorCheck(errors.category)}
                      inputProps={{
                        name: "category",
                        value: values.category,
                        placeholder: "Select type",
                        options: GROUP_CATEGORY_OPTIONS,
                        onChange: handleChange,
                        valueKey: "value",
                        labelKey: "label",
                      }}
                    />
                  </Flex>
                </Flex>

                <Spacer baselineHeight={3} />

                {animalGroup && <AssignAnimalsToGroup isEdited={isEdited} changeEditMode={changeEditMode} />}
              </PageContent>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default CreateEditGroup;
